import {
  useState,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Badge, type BadgeProps } from "@/shared/ui/core/Badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/core/Collapsible";

export type FormSectionVariant = "divider" | "panel" | "card" | "bordered" | "flat";

export type FormSectionProps = {
  title: ReactNode;
  icon?: ReactNode;
  /** Optional step number e.g. 1, 2, 3 displayed in a round badge */
  stepNumber?: number | string;
  /** Whether to show top spacing / breathing room before the section. Default: true if stepNumber > 1 */
  showDivider?: boolean;
  description?: ReactNode;
  badge?: ReactNode;
  badgeTone?: BadgeProps["tone"];
  /** Extra content on the right side of the header (actions, summary, totals) */
  headerEnd?: ReactNode;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /**
   * Visual presentation style:
   * - 'divider' (default): Modern line divider header (title + integrated horizontal line), no enclosing card border.
   * - 'flat': Flat borderless group.
   * - 'panel': Soft tinted container box.
   * - 'card': Elevated card container with full border.
   * - 'bordered': Card with accent left border.
   */
  variant?: FormSectionVariant;
  /** If true, allow user to collapse the section content. Default: false */
  collapsible?: boolean;
  defaultOpen?: boolean;
  id?: string;
};

/**
 * Standardized Section container for Form Dialogs and complex edit modals.
 * Clearly separates logical groups (e.g. Thông tin đơn, Sản phẩm, Giao hàng)
 * with a distinct header bar, framed icon badge, border container, and optional status metrics.
 */
export function FormSection({
  title,
  icon,
  stepNumber,
  showDivider,
  description,
  badge,
  badgeTone = "neutral",
  headerEnd,
  children,
  className,
  headerClassName,
  contentClassName,
  variant = "divider",
  collapsible = false,
  defaultOpen = true,
  id,
}: FormSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const isDividerVariant = variant === "divider" || variant === "flat";
  const shouldShowDivider =
    showDivider ??
    (isDividerVariant && typeof stepNumber === "number" ? stepNumber > 1 : false);

  const header = isDividerVariant ? (
    <div
      className={cn(
        "flex items-center gap-2.5 sm:gap-3 select-none",
        headerClassName,
      )}
    >
      <div className="flex shrink-0 items-center gap-2">
        {stepNumber != null && (
          <span
            className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-primary text-[12px] font-bold text-white shadow-xs"
            aria-hidden
          >
            {stepNumber}
          </span>
        )}

        {icon && (
          <span
            className="inline-flex shrink-0 items-center justify-center text-text-secondary [&>svg]:size-4"
            aria-hidden
          >
            {icon}
          </span>
        )}

        <h3 className="m-0 text-[11px] font-bold uppercase tracking-wider text-text">
          {title}
        </h3>

        {badge && (
          typeof badge === "string" || typeof badge === "number" ? (
            <Badge tone={badgeTone} label={String(badge)} />
          ) : (
            badge
          )
        )}
      </div>

      <div className="h-px flex-1 bg-border/60" aria-hidden />

      {(headerEnd || collapsible) && (
        <div className="flex shrink-0 items-center gap-2">
          {headerEnd}

          {collapsible && (
            <CollapsibleTrigger asChild>
              <button
                type="button"
                aria-label={open ? "Thu gọn phần này" : "Mở rộng phần này"}
                className="flex size-6 items-center justify-center rounded-md text-text-muted hover:bg-neutral-muted hover:text-text transition-colors cursor-pointer"
              >
                <ChevronDown
                  className={cn(
                    "size-3.5 transition-transform duration-200",
                    open && "rotate-180",
                  )}
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
          )}
        </div>
      )}
    </div>
  ) : (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2.5",
        variant === "card" && "border-b border-border/60 pb-2.5",
        variant === "bordered" && "border-b border-border/60 pb-2.5",
        variant === "panel" && "pb-0.5",
        headerClassName,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {stepNumber != null && (
          <span
            className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-primary text-[12px] font-bold text-white shadow-xs"
            aria-hidden
          >
            {stepNumber}
          </span>
        )}

        {icon && (
          <span
            className="inline-flex shrink-0 items-center justify-center size-5.5 rounded-md bg-primary-soft text-primary [&>svg]:size-3.5"
            aria-hidden
          >
            {icon}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className={cn(
                "m-0 font-bold uppercase tracking-wider text-[12px] text-text",
                variant === "panel" && "text-[11px] text-text-secondary",
              )}
            >
              {title}
            </h3>
            {badge && (
              typeof badge === "string" || typeof badge === "number" ? (
                <Badge tone={badgeTone} label={String(badge)} />
              ) : (
                badge
              )
            )}
          </div>
          {description && (
            <p className="m-0 text-[11px] text-text-muted mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {headerEnd}

        {collapsible && (
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={open ? "Thu gọn phần này" : "Mở rộng phần này"}
              className="flex size-6 items-center justify-center rounded-md text-text-muted hover:bg-neutral-muted hover:text-text transition-colors cursor-pointer"
            >
              <ChevronDown
                className={cn(
                  "size-3.5 transition-transform duration-200",
                  open && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
        )}
      </div>
    </div>
  );

  const bodyContent = (
    <div
      className={cn(
        "space-y-3 pt-1",
        variant === "card" && "space-y-3.5 pt-1",
        variant === "bordered" && "space-y-3.5 pt-1",
        contentClassName,
      )}
    >
      {isDividerVariant && description && (
        <p className="m-0 text-[11px] text-text-muted -mt-1 pb-1">
          {description}
        </p>
      )}
      {children}
    </div>
  );

  const containerClasses = cn(
    isDividerVariant && [
      "space-y-2.5 sm:space-y-3",
      shouldShowDivider && "pt-3.5 sm:pt-4",
    ],
    variant === "panel" &&
      "rounded-xl border border-border/70 bg-bg/40 dark:bg-surface-elevated/20 p-3.5 sm:p-4 space-y-3 transition-all",
    variant === "card" &&
      "rounded-xl border border-border bg-surface p-3.5 sm:p-4 shadow-2xs space-y-3.5 transition-all",
    variant === "bordered" &&
      "rounded-xl border border-border border-l-4 border-l-primary bg-surface p-3.5 sm:p-4 shadow-2xs space-y-3.5",
    className,
  );

  if (collapsible) {
    return (
      <Collapsible
        id={id}
        open={open}
        onOpenChange={setOpen}
        className={containerClasses}
      >
        {header}
        <CollapsibleContent>{bodyContent}</CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div id={id} className={containerClasses}>
      {header}
      {bodyContent}
    </div>
  );
}
